<template>
  <div class="write-article">
    <!-- Header -->
    <header class="header">
      <div class="header-inner">
        <div class="header-left">
          <div class="logo-icon"><i class="fas fa-broadcast-tower"></i></div>
          <h1>格力之声 <span>· 广播撰稿工具</span></h1>
          <div class="wave-bars"><span></span><span></span><span></span><span></span><span></span></div>
        </div>
      </div>
    </header>

    <!-- Tabs -->
    <div class="tabs-bar">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="['tab-btn', { active: activeTab === tab.key }]"
        @click="switchTab(tab.key)">
        <i :class="['tab-icon', tab.icon]"></i>
        <div>{{ tab.title }}<span class="tab-time">{{ tab.time }}</span></div>
      </button>
    </div>

    <!-- Main -->
    <div class="main-wrap">
      <div v-for="tab in tabs" :key="tab.key" v-show="activeTab === tab.key" class="tab-content">
        <div class="section-info">
          <div class="section-meta">
            <div class="meta-item"><i class="far fa-clock"></i> 播报时间：{{ CONFIG[tab.key].time }}（到达广播室：{{ CONFIG[tab.key].arrive }}）</div>
            <div class="meta-item"><i class="fas fa-font"></i> 字数要求：<span>{{ CONFIG[tab.key].minChars }} – {{ CONFIG[tab.key].maxChars }}字</span></div>
            <div class="meta-item"><i class="fas fa-compact-disc"></i> 开场音乐：<span>{{ CONFIG[tab.key].music }}</span></div>
          </div>
          <div class="guide-box">
            <div class="guide-row">
              <div class="guide-tag"><i class="fas fa-pen-nib"></i> 撰稿说明</div>
              <div class="guide-text">{{ tab.guide }}</div>
            </div>
            <div class="guide-row">
              <div class="guide-tag">
                <i class="fas fa-thumbs-up"></i> 推荐公众号
                <el-tooltip content="该链接通过搜狗微信查询查找" placement="top">
                  <i class="fas fa-circle-question guide-help"></i>
                </el-tooltip>
              </div>
              <div class="guide-text guide-accounts">
                <a v-for="(name, i) in tab.accounts" :key="i"
                   :href="getAccountUrl(name)" target="_blank" rel="noopener noreferrer"
                   :title="`点击搜索「${name}」相关文章`"
                   class="account-link">{{ name }}<i class="fas fa-arrow-up-right-from-square account-ext"></i></a>
              </div>
            </div>
          </div>
          <div class="section-fields" v-if="tab.key !== 'noon'">
            <div class="field-group">
              <label>分享主题</label>
              <el-input v-model="sections[tab.key].topic" placeholder="例：人生的取舍之道"></el-input>
            </div>
            <div class="field-group" v-if="tab.key === 'morning'">
              <label><i class="far fa-calendar"></i> 日期</label>
              <div class="date-row">
                <div class="weekday-badge">星期{{ weekdayText }}</div>
                <el-date-picker
                  v-model="broadcastDate"
                  type="date"
                  value-format="yyyy-MM-dd"
                  :clearable="false"
                  @change="onDateChange"
                  class="date-picker">
                </el-date-picker>
              </div>
              <div class="date-hint">更改日期会同步更改排版内容</div>
            </div>
          </div>
        </div>
        <div class="source-section">
          <div class="source-label"><i class="fas fa-file-lines"></i> {{ tab.sourceLabel }}</div>
          <div class="source-hint">{{ tab.sourceHint }}</div>
          <div class="source-textarea">
            <el-input
              type="textarea"
              v-model="sections[tab.key].source"
              :placeholder="tab.placeholder">
            </el-input>
          </div>
          <div class="source-actions">
            <el-button type="primary" class="btn-primary" icon="el-icon-magic-stick" @click="processContent(tab.key)">处理内容</el-button>
            <el-button class="btn-secondary" icon="el-icon-delete" @click="clearSection(tab.key)">清空</el-button>
            <div class="source-wc" v-if="getSourceCharCount(tab.key) > 0">
              <div class="source-wc-info">
                <span class="source-wc-label">原文 <i class="fas fa-file-lines"></i></span>
                <span :class="['source-wc-num', sourceWcStatus(tab.key)]">{{ getSourceCharCount(tab.key) }}</span>
                <span class="source-wc-range">/ 目标 {{ CONFIG[tab.key].minChars }}-{{ CONFIG[tab.key].maxChars }}</span>
              </div>
              <div class="source-wc-bar">
                <div class="source-wc-bar-min" :style="{ left: (CONFIG[tab.key].minChars / CONFIG[tab.key].maxChars * 100) + '%' }"></div>
                <div :class="['source-wc-fill', sourceWcStatus(tab.key)]" :style="{ width: sourceWcPct(tab.key) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="wordcount-bar" v-if="sections[tab.key].paragraphs.length > 0">
          <div class="wc-left">
            <div class="wc-total">
              <span :class="['wc-num', wcStatus(tab.key)]">{{ getSectionCharCount(tab.key) }}</span>
              <span class="wc-label">字</span>
            </div>
            <div class="wc-range">目标：<strong>{{ CONFIG[tab.key].minChars }} – {{ CONFIG[tab.key].maxChars }}</strong> 字</div>
          </div>
          <div class="wc-bar-wrap">
            <div class="wc-bar">
              <div class="wc-bar-min" :style="{ left: (CONFIG[tab.key].minChars / CONFIG[tab.key].maxChars * 100) + '%' }"></div>
              <div :class="['wc-bar-fill', wcStatus(tab.key)]" :style="{ width: wcPct(tab.key) + '%' }"></div>
            </div>
            <span class="wc-pct">{{ wcPct(tab.key) }}%</span>
          </div>
        </div>
        <div class="generated-section">
          <div class="generated-header">
            <div class="generated-title"><i class="fas fa-file-pen"></i> 生成稿件</div>
          </div>
          <div class="paragraphs-list">
            <div v-if="sections[tab.key].paragraphs.length === 0" class="empty-state">
              <i class="far fa-file-lines"></i>
              <p>{{ tab.emptyText }}</p>
            </div>
            <div
              v-for="(p, idx) in sections[tab.key].paragraphs"
              :key="idx"
              :class="['para-item', 'para-' + p.role]">
              <div class="para-label" @click="toggleRole(tab.key, idx)" :title="p.isFixed ? '固定内容' : '点击切换A/B'">{{ p.role.toUpperCase() }}</div>
              <div class="para-body">
                <textarea
                  class="para-textarea"
                  :readonly="isFixedFirstOrLast(tab.key, idx)"
                  v-model="p.text"
                  @input="autoResize($event); onParaEdit(tab.key, idx)"
                  ref="para-textareas"></textarea>
                <div class="para-footer">
                  <div :class="['para-char-count', paraRangeClass(tab.key, p)]">
                    <template v-if="p.isFixed">固定内容</template>
                    <template v-else>{{ charCount(p.text) }} 字{{ paraRangeHint(tab.key, p) }}</template>
                  </div>
                  <div class="para-actions" v-if="!isFixedFirstOrLast(tab.key, idx)">
                    <el-tooltip content="上移" placement="top">
                      <button class="btn-bar" style="padding:6px 10px" @click="movePara(tab.key, idx, -1)"><i class="fas fa-chevron-up"></i></button>
                    </el-tooltip>
                    <el-tooltip content="下移" placement="top">
                      <button class="btn-bar" style="padding:6px 10px" @click="movePara(tab.key, idx, 1)"><i class="fas fa-chevron-down"></i></button>
                    </el-tooltip>
                    <el-tooltip content="添加" placement="top">
                      <button class="btn-bar" style="padding:6px 10px" @click="insertPara(tab.key, idx)"><i class="fas fa-plus"></i></button>
                    </el-tooltip>
                    <el-tooltip content="删除" placement="top">
                      <button class="btn-bar" style="padding:6px 10px" @click="deletePara(tab.key, idx)"><i class="fas fa-xmark"></i></button>
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Toolbar -->
    <div class="bottom-bar">
      <div class="bottom-inner">
        <div class="bar-left">
          <button class="btn-bar" @click="previewAll"><i class="fas fa-eye"></i> 预览完整稿件</button>
          <button class="btn-bar" @click="copyCurrentText"><i class="fas fa-copy"></i> 复制当前时段</button>
        </div>
        <div class="bar-right">
          <button class="btn-bar" @click="saveDraft"><i class="fas fa-floppy-disk"></i> 保存草稿</button>
          <button class="btn-bar" @click="openHistory"><i class="fas fa-clock-rotate-left"></i> 历史稿件</button>
          <button class="btn-bar primary" @click="exportWord"><i class="fas fa-file-word"></i> 导出Word</button>
        </div>
      </div>
    </div>

    <!-- Preview Dialog -->
    <el-dialog
      :visible.sync="previewVisible"
      custom-class="broadcast-dialog"
      width="900px">
      <div slot="title"><i class="fas fa-eye" style="color:var(--accent);margin-right:8px"></i>完整广播稿件预览</div>
      <div>
        <div style="text-align:center;margin-bottom:24px">
          <div style="font-size:1.3rem;font-weight:700;color:var(--primary)">格力之声每日广播内容</div>
          <div style="color:var(--secondary);font-size:0.9rem;margin-top:4px">（{{ dateInfo.short }}）</div>
        </div>
        <div v-for="tab in tabs" :key="tab.key" class="preview-section">
          <div class="preview-section-title">{{ CONFIG[tab.key].title }}</div>
          <div class="preview-meta">播报时间：{{ CONFIG[tab.key].time }}（到达广播室时间：{{ CONFIG[tab.key].arrive }}）</div>
          <div class="preview-meta">开场音乐：{{ CONFIG[tab.key].music }}</div>
          <div class="preview-meta">字数：<span style="background-color:#FF0000;color:#FFFFFF;padding:1px 6px;border-radius:3px">{{ getSectionCharCount(tab.key) }}</span></div>
          <br>
          <template v-if="sections[tab.key].paragraphs.length > 0">
            <div v-for="(p, pidx) in sections[tab.key].paragraphs" :key="pidx" class="preview-line">
              <span :class="p.role === 'a' ? 'label-a' : 'label-b'">{{ p.role.toUpperCase() }}：</span>{{ p.text }}
            </div>
          </template>
          <div v-else style="color:var(--secondary);font-style:italic">（未生成内容）</div>
        </div>
      </div>
      <template #footer>
        <el-button class="btn-secondary" icon="el-icon-document-copy" @click="copyPreviewText">复制全部文本</el-button>
        <el-button type="primary" class="btn-primary" icon="el-icon-document" @click="exportWord">导出Word</el-button>
      </template>
    </el-dialog>

    <!-- History Dialog -->
    <el-dialog
      :visible.sync="historyVisible"
      custom-class="broadcast-dialog"
      width="700px">
      <div slot="title"><i class="fas fa-clock-rotate-left" style="color:var(--accent);margin-right:8px"></i>历史稿件</div>
      <div>
        <div v-if="historyList.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <p>暂无历史稿件</p>
        </div>
        <div v-else class="history-list">
          <div v-for="h in historyList" :key="h.key" class="history-item">
            <div>
              <div class="history-date">{{ h.date }}</div>
              <div class="history-status">
                <span :class="['dot', h.statusClass]"></span>{{ h.statusText }} · 保存于 {{ h.savedTime }}
              </div>
            </div>
            <div class="history-actions">
              <el-tooltip content="加载" placement="top">
                <button class="btn-bar" style="padding:6px 10px" @click="loadHistory(h.key)"><i class="fas fa-download"></i></button>
              </el-tooltip>
              <el-tooltip content="删除" placement="top">
                <button class="btn-bar" style="padding:6px 10px" @click="deleteHistory(h.key)"><i class="fas fa-trash"></i></button>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'WriteArticle',
  data() {
    return {
      activeTab: 'morning',
      broadcastDate: '',
      previewVisible: false,
      historyVisible: false,
      historyList: [],
      WEEKDAYS: ['日','一','二','三','四','五','六'],
      CONFIG: {
        morning: {
          title: '早间播报',
          time: '8：10——8：30',
          arrive: '7：55',
          music: 'Sundial Dreams',
          minChars: 1200,
          maxChars: 2000,
          paraMin: 80,
          paraMax: 140,
          opening: (date, weekday, topic) => `各位同事，大家上午好！今天是${date}，星期${weekday}。今天分享的主题是：${topic || '（请填写主题）'}。`,
          closing: '以上就是今天的广播内容，感谢大家的收听，各位听众朋友，我们中午见。'
        },
        noon: {
          title: '新闻速递',
          time: '11：45——12：15',
          arrive: '11：30',
          music: 'New Light (Mark Light)',
          minChars: 1500,
          maxChars: 2000,
          paraMin: 80,
          paraMax: 140,
          opening: (date, weekday, topic) => `各位听众朋友，大家中午好，盘点省内动态，纵观时事热点。我是今天的播音员xxx，我是xxx。现在是新闻速递时间，下面请听详细内容。`,
          closing: '以上就是今天的新闻内容，感谢大家的收听，各位听众朋友，我们下午见。'
        },
        afternoon: {
          title: '愉快生活',
          time: '17：30——18：00',
          arrive: '17：15',
          music: 'Trinity',
          minChars: 1200,
          maxChars: 2000,
          paraMin: 80,
          paraMax: 140,
          opening: (date, weekday, topic) => `各位同事下午好！快乐工作，健康生活。今天下午分享的主题是：${topic || '（请填写主题）'}`,
          closing: '以上就是今天的内容，各位听众朋友，我们下期见！'
        }
      },
      tabs: [
        {
          key: 'morning', title: '早间播报', time: '8:10 – 8:30',
          icon: 'fas fa-sun',
          sourceLabel: '原文内容',
          sourceHint: '从推荐公众号复制原文粘贴到此处，工具将自动分段并标注A/B',
          placeholder: '请粘贴原文内容...\n\n提示：建议粘贴 2000-2500 字的原文，工具会自动处理为符合要求的广播稿件格式。',
          emptyText: '粘贴原文后点击「处理内容」生成稿件',
          guide: '以"诗歌、散文、随笔"为主，用灼热的观点启迪思想，用温暖的文字抚慰心灵。',
          accounts: ['洞见', '人民日报', '南方周末', '三联生活周刊']
        },
        {
          key: 'noon', title: '新闻速递', time: '11:45 – 12:15',
          icon: 'fas fa-newspaper',
          sourceLabel: '新闻原文',
          sourceHint: '从推荐公众号复制省内新闻原文粘贴到此处',
          placeholder: '请粘贴新闻原文...\n\n提示：可粘贴多篇新闻，工具会自动合并处理。',
          emptyText: '粘贴新闻原文后点击「处理内容」生成稿件',
          guide: '以"新闻热点"为主，盘点省内动态变化，纵观省内时事热点，突出本地化、生活化，聚焦广东省内时政、民生、社会、文化及贴近员工生活的喜闻乐见内容。',
          accounts: ['人民日报', '珠海发布', '广东共青团', '共青团中央', '学习强国']
        },
        {
          key: 'afternoon', title: '愉快生活', time: '17:30 – 18:00',
          icon: 'fas fa-heart-pulse',
          sourceLabel: '养生原文',
          sourceHint: '从推荐公众号复制健康类原文粘贴到此处',
          placeholder: '请粘贴养生健康类原文...\n\n提示：建议粘贴 2000-2500 字的原文。',
          emptyText: '粘贴养生原文后点击「处理内容」生成稿件',
          guide: '以"运动、健康、娱乐、旅游、休闲类"为主，用愉快的心情释放心声，用声音的魅力紧扣生活。',
          accounts: ['好奇博士', '健康中国', '生命时报']
        }
      ],
      sections: {
        morning: { topic: '', source: '', paragraphs: [] },
        noon: { topic: '', source: '', paragraphs: [] },
        afternoon: { topic: '', source: '', paragraphs: [] }
      }
    };
  },
  computed: {
    weekdayText() {
      if (!this.broadcastDate) return '';
      const d = new Date(this.broadcastDate);
      return this.WEEKDAYS[d.getDay()];
    },
    dateInfo() {
      const val = this.broadcastDate;
      const d = new Date(val);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return {
        value: val,
        cn: `${y}年${m}月${day}日`,
        short: `${String(m).padStart(2,'0')}${String(day).padStart(2,'0')}`,
        weekday: this.WEEKDAYS[d.getDay()]
      };
    }
  },
  mounted() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.broadcastDate = this.formatDateValue(tomorrow);
    this._oldDate = this.broadcastDate;

    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    // 自动保存当前草稿
    this.saveDraftToStorage();
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  updated() {
    this.$nextTick(() => this.autoResizeAll());
  },
  methods: {
    handleKeydown(e) {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 's') { e.preventDefault(); this.saveDraft(); }
      }
      if (e.key === 'Escape') {
        if (this.previewVisible) this.previewVisible = false;
        if (this.historyVisible) this.historyVisible = false;
      }
    },
    handleBeforeUnload() {
      this.saveDraftToStorage();
    },
    formatDateValue(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    onDateChange() {
      // 先保存旧日期的草稿
      if (this._oldDate && this._oldDate !== this.broadcastDate) {
        this.saveDraftToStorage(this._oldDate);
      }
      this._oldDate = this.broadcastDate;
      // 更新包含日期的开场段落
      ['morning','noon','afternoon'].forEach(sec => {
        const paras = this.sections[sec].paragraphs;
        if (paras.length > 0 && paras[0].isFixed) {
          const cfg = this.CONFIG[sec];
          paras[0].text = cfg.opening(this.dateInfo.cn, this.dateInfo.weekday, this.sections[sec].topic);
        }
      });
    },
    switchTab(tab) { this.activeTab = tab; },

    // ===== Account Search URL =====
    getAccountUrl(name) {
      return `https://weixin.sogou.com/weixin?type=2&query=${encodeURIComponent(name)}&ie=utf8`;
    },

    // ===== Character Counting =====
    charCount(text) {
      if (!text) return 0;
      return text.replace(/[\s\r\n]/g, '').length;
    },

    // ===== Text Processing =====
    splitIntoParagraphs(text, minC, maxC) {
      if (!text || !text.trim()) return [];
      text = text.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
      let rawParas = text.split(/\n+/).map(p => p.trim()).filter(p => p.length > 0);
      let result = [];
      for (const para of rawParas) {
        const len = this.charCount(para);
        if (len <= maxC) {
          result.push(para);
        } else {
          let sentences = para.match(/[^。！？\n]*[。！？\n]/g) || [];
          const matched = sentences.join('');
          if (matched.length < para.length) {
            const remaining = para.substring(matched.length).trim();
            if (remaining) sentences.push(remaining);
          }
          if (sentences.length === 0) sentences = [para];
          let current = '';
          for (const s of sentences) {
            const trimmed = s.trim();
            if (!trimmed) continue;
            const testLen = this.charCount(current + trimmed);
            if (testLen > maxC && this.charCount(current) >= minC) {
              result.push(current.trim());
              current = trimmed;
            } else if (testLen > maxC && this.charCount(current) < minC) {
              if (current.trim()) result.push(current.trim());
              current = trimmed;
            } else {
              current += trimmed;
            }
          }
          if (current.trim()) result.push(current.trim());
        }
      }
      if (result.length <= 1) return result;
      let merged = [...result];
      let changed = true;
      let safety = 20;
      while (changed && merged.length > 1 && safety-- > 0) {
        changed = false;
        let next = [];
        let i = 0;
        while (i < merged.length) {
          const cc = this.charCount(merged[i]);
          if (cc < minC) {
            if (i + 1 < merged.length) {
              next.push(merged[i] + merged[i + 1]);
              i += 2;
            } else if (next.length > 0) {
              next[next.length - 1] = next[next.length - 1] + merged[i];
              i++;
            } else {
              next.push(merged[i]);
              i++;
            }
            changed = true;
          } else {
            next.push(merged[i]);
            i++;
          }
        }
        merged = next;
      }
      let final = [];
      for (const p of merged) {
        if (this.charCount(p) <= maxC) {
          final.push(p);
        } else {
          const subs = this.splitAtSentence(p, maxC);
          final.push(...subs);
        }
      }
      if (final.length > 1) {
        changed = true;
        safety = 20;
        while (changed && final.length > 1 && safety-- > 0) {
          changed = false;
          let next = [];
          let i = 0;
          while (i < final.length) {
            const cc = this.charCount(final[i]);
            if (cc < minC) {
              if (i + 1 < final.length) {
                next.push(final[i] + final[i + 1]);
                i += 2;
              } else if (next.length > 0) {
                next[next.length - 1] = next[next.length - 1] + final[i];
                i++;
              } else {
                next.push(final[i]);
                i++;
              }
              changed = true;
            } else {
              next.push(final[i]);
              i++;
            }
          }
          final = next;
        }
      }
      return final;
    },

    splitAtSentence(text, maxLen) {
      if (this.charCount(text) <= maxLen) return [text];
      const sentences = text.match(/[^。！？]*[。！？]/g) || [];
      const matched = sentences.join('');
      const remaining = text.substring(matched.length).trim();
      if (remaining) sentences.push(remaining);
      let result = [];
      let current = '';
      for (const s of sentences) {
        if (this.charCount(current + s) > maxLen && current) {
          result.push(current.trim());
          current = s;
        } else {
          current += s;
        }
      }
      if (current.trim()) result.push(current.trim());
      return result;
    },

    processContent(section) {
      const text = (this.sections[section].source || '').trim();
      if (!text) {
        this.$message({ message: '请先粘贴原文内容', type: 'error' });
        return;
      }
      const topic = (this.sections[section].topic || '').trim();
      if (section !== 'noon' && !topic) {
        this.$message({ message: '请先输入分享主题', type: 'error' });
        return;
      }
      if (section === 'morning') {
        this.$message({ message: `正在生成${this.dateInfo.cn}的稿件`, type: 'info' });
      }
      const cfg = this.CONFIG[section];
      const paragraphs = this.splitIntoParagraphs(text, cfg.paraMin, cfg.paraMax);
      if (paragraphs.length === 0) {
        this.$message({ message: '无法处理内容，请检查原文', type: 'error' });
        return;
      }
      const opening = cfg.opening(this.dateInfo.cn, this.dateInfo.weekday, topic);
      let paras = [];
      paras.push({ role: 'a', text: opening, isFixed: true });
      for (let i = 0; i < paragraphs.length; i++) {
        paras.push({ role: i % 2 === 0 ? 'b' : 'a', text: paragraphs[i], isFixed: false });
      }
      const closingRole = paras.length % 2 === 0 ? 'a' : 'b';
      paras.push({ role: closingRole, text: cfg.closing, isFixed: true });
      this.sections[section].paragraphs = paras;
      this.$message({ message: `已生成 ${paragraphs.length} 个内容段落`, type: 'success' });
    },

    // ===== Paragraph helpers =====
    isFixedFirstOrLast(section, idx) {
      const paras = this.sections[section].paragraphs;
      const p = paras[idx];
      if (!p) return false;
      const isFirst = idx === 0;
      const isLast = idx === paras.length - 1;
      return p.isFixed && (isFirst || isLast);
    },
    paraRangeClass(section, p) {
      if (p.isFixed) return '';
      const cfg = this.CONFIG[section];
      const cc = this.charCount(p.text);
      if (cc >= cfg.paraMin && cc <= cfg.paraMax) return 'in-range';
      if (cc > cfg.paraMax) return 'over';
      return 'under';
    },
    paraRangeHint(section, p) {
      if (p.isFixed) return '';
      const cfg = this.CONFIG[section];
      const cc = this.charCount(p.text);
      if (cc >= cfg.paraMin && cc <= cfg.paraMax) return ' ✓';
      if (cc > cfg.paraMax) return ' (超出)';
      return '';
    },
    onParaEdit() {
      // Update triggered automatically via v-model; nothing else to do here.
    },
    toggleRole(section, idx) {
      const p = this.sections[section].paragraphs[idx];
      if (!p || p.isFixed) return;
      p.role = p.role === 'a' ? 'b' : 'a';
    },
    movePara(section, idx, dir) {
      const paras = this.sections[section].paragraphs;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= paras.length) return;
      if (paras[idx].isFixed || paras[newIdx].isFixed) return;
      const tmp = paras[idx];
      this.$set(paras, idx, paras[newIdx]);
      this.$set(paras, newIdx, tmp);
      this.rebalanceRoles(section);
    },
    deletePara(section, idx) {
      this.sections[section].paragraphs.splice(idx, 1);
      this.rebalanceRoles(section);
    },
    insertPara(section, afterIdx) {
      this.sections[section].paragraphs.splice(afterIdx + 1, 0, { role: 'a', text: '', isFixed: false });
      this.rebalanceRoles(section);
    },
    rebalanceRoles(section) {
      const paras = this.sections[section].paragraphs;
      paras.forEach((p, i) => {
        p.role = i % 2 === 0 ? 'a' : 'b';
      });
    },

    // ===== Word Count =====
    getSectionCharCount(section) {
      let total = 0;
      this.sections[section].paragraphs.forEach(p => { if (!p.isFixed) total += this.charCount(p.text); });
      return total;
    },
    wcStatus(section) {
      const total = this.getSectionCharCount(section);
      const cfg = this.CONFIG[section];
      if (total < cfg.minChars) return total >= cfg.minChars * 0.8 ? 'warn' : 'bad';
      if (total > cfg.maxChars) return 'bad';
      return 'good';
    },
    wcPct(section) {
      const total = this.getSectionCharCount(section);
      const cfg = this.CONFIG[section];
      return Math.min(100, Math.max(0, Math.round((total / cfg.maxChars) * 100)));
    },

    // ===== Source Word Count =====
    getSourceCharCount(section) {
      return this.charCount(this.sections[section].source);
    },
    sourceWcStatus(section) {
      const total = this.getSourceCharCount(section);
      const cfg = this.CONFIG[section];
      if (total === 0) return 'bad';
      if (total < cfg.minChars) return total >= cfg.minChars * 0.8 ? 'warn' : 'bad';
      return 'good';
    },
    sourceWcPct(section) {
      const total = this.getSourceCharCount(section);
      const cfg = this.CONFIG[section];
      return Math.min(100, Math.max(0, Math.round((total / cfg.maxChars) * 100)));
    },

    clearSection(section) {
      this.sections[section].source = '';
      this.sections[section].topic = '';
      this.sections[section].paragraphs = [];
    },

    // ===== Auto-resize textareas =====
    autoResize(ev) {
      const ta = ev && ev.target;
      if (!ta) return;
      ta.style.height = 'auto';
      ta.style.height = (ta.scrollHeight + 2) + 'px';
    },
    autoResizeAll() {
      const els = this.$refs['para-textareas'];
      if (!els) return;
      const list = Array.isArray(els) ? els : [els];
      list.forEach(ta => {
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = (ta.scrollHeight + 2) + 'px';
      });
    },

    // ===== Preview =====
    generatePreviewText(section) {
      const cfg = this.CONFIG[section];
      const paras = this.sections[section].paragraphs;
      let text = `${cfg.title}\n\n`;
      text += `播报时间：${cfg.time}（到达广播室时间：${cfg.arrive}）\n\n`;
      text += `开场音乐：${cfg.music}\n\n`;
      text += `字数：${this.getSectionCharCount(section)}\n\n`;
      paras.forEach(p => {
        text += `${p.role.toUpperCase()}：${p.text}\n\n`;
      });
      return text;
    },
    previewAll() {
      this.previewVisible = true;
    },
    copyCurrentText() {
      const section = this.activeTab;
      if (this.sections[section].paragraphs.length === 0) {
        this.$message({ message: '当前时段暂无内容', type: 'error' });
        return;
      }
      const text = this.generatePreviewText(section);
      this.copyToClipboard(text);
    },
    copyPreviewText() {
      let text = `格力之声每日广播内容（${this.dateInfo.short}）\n\n`;
      ['morning','noon','afternoon'].forEach(sec => {
        text += this.generatePreviewText(sec) + '\n';
      });
      this.copyToClipboard(text);
    },
    copyToClipboard(text) {
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(ta);
        this.$message({ message: '已复制到剪贴板', type: 'success' });
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message({ message: '已复制到剪贴板', type: 'success' }))
          .catch(fallback);
      } else {
        fallback();
      }
    },

    // ===== Export Word =====
    exportWord() {
      const dateInfo = this.dateInfo;
      let content = '';
      const sections = ['morning','noon','afternoon'];
      content += `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
  <head><meta charset="UTF-8">
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>
    @page Section1 {
      size: 595.3pt 841.9pt;
      margin: 70.87pt 56.69pt 56.69pt 70.87pt;
      mso-header-margin: 42.55pt;
      mso-footer-margin: 49.6pt;
      mso-paper-source: 0;
    }
    div.Section1 { page: Section1; }
    body { font-family: '仿宋', FangSong, '仿宋_GB2312', STFangsong, serif; font-size: 16pt; color: #000; }
    p { font-family: '仿宋', FangSong, '仿宋_GB2312', STFangsong, serif; font-size: 16pt; mso-line-height-rule: auto; line-height: 150%; margin: 0pt; }
    .main-title { text-align: center; font-family: '华文中宋', STZhongsong, serif; font-size: 18pt; line-height: 150%; margin-bottom: 12pt; }
    .section-title { font-family: '仿宋', FangSong, '仿宋_GB2312', STFangsong, serif; font-size: 16pt; font-weight: bold; line-height: 150%; margin-top: 12pt; margin-bottom: 6pt; text-indent: 32pt; color: #0000FF; }
    .meta { font-family: '仿宋', FangSong, '仿宋_GB2312', STFangsong, serif; font-size: 16pt; font-weight: bold; line-height: 150%; text-indent: 32pt; }
    .meta-arrive { color: #0000FF; }
    .line { font-family: '仿宋', FangSong, '仿宋_GB2312', STFangsong, serif; font-size: 16pt; line-height: 150%; margin-bottom: 0pt; text-indent: 32pt; }
    .page-break-before { page-break-before: always; }
  </style></head><body><div class="Section1">`;

      const titleDate = dateInfo.value.replace(/-/g, '');
      content += `<p style="text-align:center;font-family:'宋体',SimSun,serif;font-size:18pt;line-height:1;margin-bottom:12pt">格力之声每日广播内容（${titleDate}）</p>`;

      sections.forEach((sec, idx) => {
        const cfg = this.CONFIG[sec];
        const paras = this.sections[sec].paragraphs;
        if (idx > 0) {
          content += `<br clear="all" style="page-break-before:always">`;
        }
        content += `<p style="font-family:'仿宋',FangSong,'仿宋_GB2312',STFangsong,serif;font-size:16pt;font-weight:bold;line-height:150%;margin-top:12pt;margin-bottom:6pt;text-indent:32pt;color:#0000FF">${cfg.title}</p>`;
        content += `<p style="font-family:'仿宋',FangSong,'仿宋_GB2312',STFangsong,serif;font-size:16pt;font-weight:bold;line-height:150%;text-indent:32pt">播报时间：${cfg.time} <span style="color:#0000FF">（到达广播室时间：${cfg.arrive}）</span></p>`;
        content += `<p style="font-family:'仿宋',FangSong,'仿宋_GB2312',STFangsong,serif;font-size:16pt;font-weight:bold;line-height:150%;text-indent:32pt">开场音乐：${cfg.music}</p>`;
        content += `<p style="font-family:'仿宋',FangSong,'仿宋_GB2312',STFangsong,serif;font-size:16pt;font-weight:bold;line-height:150%;text-indent:32pt">字数：<span style="background-color:#FF0000;color:#FFFFFF">${this.getSectionCharCount(sec)}</span></p>`;
        if (paras.length > 0) {
          paras.forEach(p => {
            content += `<p style="font-family:'仿宋',FangSong,'仿宋_GB2312',STFangsong,serif;font-size:16pt;line-height:150%;margin:0pt;text-indent:32pt"><b>${p.role.toUpperCase()}：</b>${this.escapeHtml(p.text)}</p>`;
          });
        }
      });
      content += `</div></body></html>`;

      const blob = new Blob(['\ufeff' + content], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `格力之声广播稿件_${dateInfo.value}.doc`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.$message({ message: 'Word文档已导出', type: 'success' });
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text == null ? '' : String(text);
      return div.innerHTML;
    },

    // ===== Draft Management =====
    saveDraft() {
      this.saveDraftToStorage();
      this.$message({ message: '草稿已保存', type: 'success' });
    },
    saveDraftToStorage(dateValue) {
      const val = dateValue || this.dateInfo.value;
      if (!val) return;
      const key = `broadcast_draft_${val}`;
      const data = {
        date: val,
        savedAt: new Date().toISOString(),
        sections: {}
      };
      ['morning','noon','afternoon'].forEach(sec => {
        data.sections[sec] = {
          topic: this.sections[sec].topic,
          source: this.sections[sec].source,
          paragraphs: this.sections[sec].paragraphs
        };
      });
      try {
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e) {
        console.error('保存草稿失败:', e);
      }
    },
    autoLoadDraft() {
      const dateInfo = this.dateInfo;
      if (!dateInfo.value) return;
      const key = `broadcast_draft_${dateInfo.value}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          const data = JSON.parse(raw);
          this.loadDraftData(data);
          this.$message({ message: '已自动加载今日草稿', type: 'success' });
        } catch (e) {
          console.error('加载草稿失败:', e);
        }
      }
    },
    loadDraftData(data) {
      if (!data || !data.sections) return;
      ['morning','noon','afternoon'].forEach(sec => {
        const s = data.sections[sec];
        if (!s) return;
        this.sections[sec].topic = s.topic || '';
        this.sections[sec].source = s.source || '';
        const newParagraphs = (s.paragraphs || []).map(p => ({
          role: p.role || 'a',
          text: p.text || '',
          isFixed: !!p.isFixed
        }));
        this.$set(this.sections[sec], 'paragraphs', newParagraphs);
      });
    },
    openHistory() {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('broadcast_draft_')) keys.push(k);
      }
      keys.sort().reverse();
      this.historyList = keys.map(k => {
        let data = {};
        try { data = JSON.parse(localStorage.getItem(k)); } catch (e) {}
        const date = k.replace('broadcast_draft_', '');
        let doneCount = 0;
        if (data.sections) {
          Object.keys(data.sections).forEach(sec => {
            if (data.sections[sec] && data.sections[sec].paragraphs && data.sections[sec].paragraphs.length > 0) doneCount++;
          });
        }
        const statusClass = doneCount === 3 ? 'done' : doneCount > 0 ? 'partial' : '';
        const statusText = doneCount === 3 ? '已完成' : `${doneCount}/3 时段`;
        const savedTime = data.savedAt
          ? new Date(data.savedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
          : '';
        return { key: k, date, statusClass, statusText, savedTime };
      });
      this.historyVisible = true;
    },
    loadHistory(key) {
      const raw = localStorage.getItem(key);
      if (!raw) {
        this.$message({ message: '稿件不存在', type: 'error' });
        return;
      }
      try {
        const data = JSON.parse(raw);
        if (!data || !data.sections) {
          this.$message({ message: '稿件数据损坏', type: 'error' });
          return;
        }
        // 切换日期前保存当前草稿
        if (this._oldDate && this._oldDate !== data.date) {
          this.saveDraftToStorage(this._oldDate);
        }
        this.broadcastDate = data.date;
        this._oldDate = data.date;
        this.$nextTick(() => {
          this.onDateChange();
          this.loadDraftData(data);
          this.historyVisible = false;
          this.$message({ message: '已加载历史稿件', type: 'success' });
        });
      } catch (e) {
        this.$message({ message: '加载失败', type: 'error' });
      }
    },
    deleteHistory(key) {
      this.$confirm('确定要删除这份稿件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        localStorage.removeItem(key);
        this.openHistory();
        this.$message({ message: '已删除', type: 'success' });
      }).catch(() => {});
    }
  }
};
</script>

<style>
:root {
  --bg: #f5f0e8;
  --surface: #ffffff;
  --primary: #1e293b;
  --secondary: #64748b;
  --accent: #b45309;
  --accent-light: #fef3c7;
  --accent-hover: #92400e;
  --border: #e2d9cc;
  --border-light: #ede7db;
  --success: #059669;
  --success-bg: #ecfdf5;
  --warning: #d97706;
  --warning-bg: #fffbeb;
  --error: #dc2626;
  --error-bg: #fef2f2;
  --a-color: #b45309;
  --a-bg: #fffbeb;
  --b-color: #0369a1;
  --b-bg: #f0f9ff;
  --shadow-sm: 0 1px 3px rgba(0,0,0,0.06);
  --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
  --shadow-lg: 0 8px 30px rgba(0,0,0,0.12);
  --radius: 10px;
  --radius-sm: 6px;
}
.write-article *, .write-article *::before, .write-article *::after {
  box-sizing: border-box; margin: 0; padding: 0;
}
.write-article {
  font-size: 15px;
  scroll-behavior: smooth;
  background: var(--bg);
  color: var(--primary);
  line-height: 1.7;
  min-height: 100vh;
  position: relative;
}
.write-article::before {
  content: ''; position: fixed; inset: 0; z-index: -1;
  background:
    radial-gradient(ellipse at 20% 50%, rgba(180,83,9,0.04), transparent 60%),
    radial-gradient(ellipse at 80% 20%, rgba(3,105,161,0.03), transparent 50%),
    linear-gradient(180deg, #f5f0e8 0%, #ede7db 100%);
}
/* ===== Header ===== */
.header {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #fff; padding: 0; position: sticky; top: 0; z-index: 100;
  box-shadow: 0 2px 20px rgba(0,0,0,0.15);
}
.header-inner {
  max-width: 1200px; margin: 0 auto; padding: 16px 24px;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
}
.header-left { display: flex; align-items: center; gap: 16px; }
.logo-icon {
  width: 44px; height: 44px; border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: #fff; flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(180,83,9,0.3);
}
.header h1 {
  font-family: 'SimHei', 'Microsoft YaHei', 'PingFang SC', sans-serif;
  font-size: 1.5rem; font-weight: 700; letter-spacing: 0.5px;
}
.header h1 span { color: var(--accent); font-weight: 600; }
/* ===== Tabs ===== */
.tabs-bar {
  max-width: 1200px; margin: 0 auto; padding: 20px 24px 0;
  display: flex; gap: 6px; position: relative;
}
.tab-btn {
  flex: 1; padding: 14px 20px; border: none; background: var(--surface);
  border-radius: var(--radius) var(--radius) 0 0;
  font-family: inherit; font-size: 0.95rem; font-weight: 500;
  color: var(--secondary); cursor: pointer; transition: all 0.25s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid var(--border); border-bottom: none;
  position: relative;
}
.tab-btn:hover { color: var(--primary); background: #fefcf8; }
.tab-btn.active {
  background: var(--surface); color: var(--accent);
  font-weight: 600; box-shadow: 0 -2px 8px rgba(180,83,9,0.1);
  z-index: 2; border-color: var(--accent); border-bottom: 2px solid var(--surface);
}
.tab-btn .tab-icon { font-size: 1.1rem; }
.tab-btn .tab-time { font-size: 0.75rem; color: var(--secondary); display: block; margin-top: 2px; }
.tab-btn.active .tab-time { color: var(--accent); opacity: 0.7; }
/* ===== Main Content ===== */
.main-wrap { max-width: 1200px; margin: 0 auto; padding: 0 24px 100px; }
.tab-content {
  background: var(--surface);
  border: 1px solid var(--border); border-top: none;
  border-radius: 0 0 var(--radius) var(--radius);
  box-shadow: var(--shadow-md); overflow: hidden;
}
/* ===== Section Info ===== */
.section-info {
  padding: 20px 28px; background: linear-gradient(135deg, #fefcf8, #fff);
  border-bottom: 1px solid var(--border-light);
}
.section-meta { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.meta-item { display: flex; align-items: center; gap: 6px; font-size: 0.85rem; color: var(--secondary); }
.meta-item i { color: var(--accent); width: 16px; text-align: center; }
.meta-item span { color: var(--primary); font-weight: 500; }
.section-fields { display: grid; grid-template-columns: 1fr; gap: 12px; }
.field-group { display: flex; flex-direction: column; gap: 4px; }
.field-group label { font-size: 0.8rem; color: var(--secondary); font-weight: 500; }
.field-group .el-input__inner { height: 36px; line-height: 36px; }
.date-row { display: flex; align-items: center; gap: 10px; }
.date-row .date-picker { width: 160px; flex-shrink: 0; }
.date-row .date-picker .el-input__inner { height: 36px; line-height: 36px; }
.date-hint { font-size: 0.72rem; color: var(--secondary); opacity: 0.7; margin-top: 2px; }
.weekday-badge {
  background: rgba(180,83,9,0.1); border: 1px solid rgba(180,83,9,0.25);
  color: var(--accent); padding: 8px 14px; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600; white-space: nowrap; flex-shrink: 0;
}
/* ===== Guide Box ===== */
.guide-box {
  margin-top: 4px; padding: 12px 16px;
  background: linear-gradient(135deg, #fefcf8, #fffbeb);
  border: 1px dashed rgba(180,83,9,0.25); border-radius: var(--radius-sm);
  display: flex; flex-direction: column; gap: 10px;
}
.guide-row { display: flex; align-items: flex-start; gap: 10px; }
.guide-tag {
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 12px; font-size: 0.78rem; font-weight: 600;
  color: var(--accent); background: rgba(180,83,9,0.1);
  white-space: nowrap; margin-top: 1px;
}
.guide-tag i { font-size: 0.8rem; }
.guide-help {
  font-size: 0.78rem; opacity: 0.5; cursor: help; transition: opacity 0.2s;
}
.guide-help:hover { opacity: 1; }
.guide-text {
  flex: 1; font-size: 0.85rem; line-height: 1.7; color: var(--primary);
}
.guide-accounts { display: flex; flex-wrap: wrap; align-items: center; gap: 2px; }
.account-link {
  display: inline-flex; align-items: center; gap: 3px;
  color: var(--b-color); font-weight: 500; text-decoration: none;
  padding: 1px 4px; border-radius: 4px; transition: all 0.2s; cursor: pointer;
}
.account-link:hover {
  color: #fff; background: var(--b-color); text-decoration: none;
}
.account-link:not(:last-child)::after { content: '、'; color: var(--secondary); margin-right: 2px; }
.account-link:hover:not(:last-child)::after { color: #fff; }
.account-ext { font-size: 0.62rem; opacity: 0.55; }
.account-link:hover .account-ext { opacity: 0.85; }
/* ===== Source Input ===== */
.source-section { padding: 20px 28px; border-bottom: 1px solid var(--border-light); }
.source-label { font-size: 0.9rem; font-weight: 600; color: var(--primary); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.source-label i { color: var(--accent); }
.source-hint { font-size: 0.78rem; color: var(--secondary); margin-bottom: 12px; }
.source-textarea .el-textarea__inner {
  min-height: 180px !important; font-family: inherit; font-size: 0.9rem;
  line-height: 1.8; background: #fafaf7; border-color: var(--border);
  color: var(--primary);
}
.source-textarea .el-textarea__inner:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(180,83,9,0.08); }
.source-actions { display: flex; gap: 10px; margin-top: 14px; align-items: center; }
.source-actions .el-button {
  padding: 12px 20px;
}
/* ===== Source Word Count ===== */
.source-wc {
  margin-left: auto; display: flex; flex-direction: column; align-items: flex-end; gap: 5px;
  min-width: 160px; padding: 6px 12px; background: #f8fafc;
  border: 1px solid var(--border-light); border-radius: var(--radius-sm);
}
.source-wc-info { display: flex; align-items: baseline; gap: 4px; flex-wrap: wrap; justify-content: flex-end; }
.source-wc-label { font-size: 0.72rem; color: var(--secondary); display: inline-flex; align-items: center; gap: 3px; }
.source-wc-label i { font-size: 0.7rem; }
.source-wc-num { font-size: 1.05rem; font-weight: 700; transition: color 0.3s; }
.source-wc-num.good { color: var(--success); }
.source-wc-num.warn { color: var(--warning); }
.source-wc-num.bad { color: var(--error); }
.source-wc-range { font-size: 0.7rem; color: var(--secondary); }
.source-wc-bar { width: 100%; height: 6px; background: var(--border); border-radius: 3px; overflow: hidden; position: relative; }
.source-wc-bar-min { position: absolute; top: -2px; bottom: -2px; width: 2px; background: var(--warning); border-radius: 1px; z-index: 1; }
.source-wc-fill { height: 100%; border-radius: 3px; transition: width 0.4s ease, background 0.3s; position: relative; z-index: 2; }
.source-wc-fill.good { background: var(--success); }
.source-wc-fill.warn { background: var(--warning); }
.source-wc-fill.bad { background: var(--error); }
/* Custom buttons to match original */
.btn-primary {
  background: linear-gradient(135deg, var(--accent), #d97706) !important;
  border: none !important; color: #fff !important;
  box-shadow: 0 2px 8px rgba(180,83,9,0.25);
}
.btn-primary:hover,
.btn-primary:focus {
  background: linear-gradient(135deg, var(--accent-hover), var(--accent)) !important;
  transform: translateY(-1px); box-shadow: 0 4px 12px rgba(180,83,9,0.3);
}
.btn-secondary {
  background: var(--border-light) !important;
  color: var(--secondary) !important; border: none !important;
}
.btn-secondary:hover,
.btn-secondary:focus {
  background: var(--border) !important; color: var(--primary) !important;
}
/* ===== Generated Content ===== */
.generated-section { padding: 20px 28px; }
.generated-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.generated-title { font-size: 0.95rem; font-weight: 600; color: var(--primary); display: flex; align-items: center; gap: 6px; }
.generated-title i { color: var(--accent); }
.paragraphs-list { display: flex; flex-direction: column; gap: 10px; }
.para-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 16px; border-radius: var(--radius-sm);
  border-left: 3px solid transparent;
  transition: all 0.2s; position: relative;
}
.para-item:hover { box-shadow: var(--shadow-sm); }
.para-item.para-a { background: var(--a-bg); border-left-color: var(--a-color); }
.para-item.para-b { background: var(--b-bg); border-left-color: var(--b-color); }
.para-label {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.85rem; margin-top: 2px;
  cursor: pointer; color: #fff; user-select: none;
}
.para-a .para-label { background: var(--a-color); }
.para-b .para-label { background: var(--b-color); }
.para-body { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.para-textarea {
  width: 100%; padding: 8px 10px;
  border: 1px solid transparent; border-radius: 4px;
  font-family: inherit; font-size: 0.9rem; line-height: 1.8;
  color: var(--primary); background: rgba(255,255,255,0.6);
  resize: none; outline: none; transition: all 0.2s;
  overflow: hidden;
}
.para-textarea:focus { border-color: var(--accent); background: #fff; }
.para-textarea[readonly] { background: #f0f4f8; cursor: default; color: var(--secondary); }
.para-footer { display: flex; align-items: center; justify-content: space-between; }
.para-char-count { font-size: 0.75rem; color: var(--secondary); }
.para-char-count.in-range { color: var(--success); }
.para-char-count.over { color: var(--error); }
.para-char-count.under { color: var(--warning); }
.para-actions { display: flex; gap: 4px; }
.empty-state { text-align: center; padding: 40px 20px; color: var(--secondary); }
.empty-state i { font-size: 2.5rem; color: var(--border); margin-bottom: 12px; display: block; }
.empty-state p { font-size: 0.9rem; }
/* ===== Word Count Bar ===== */
.wordcount-bar {
  margin: 20px 28px; padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border: 1px solid var(--border-light); border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
}
.wc-left { display: flex; align-items: center; gap: 16px; }
.wc-total { display: flex; align-items: baseline; gap: 6px; }
.wc-total .wc-num { font-size: 1.5rem; font-weight: 700; }
.wc-total .wc-num.good { color: var(--success); }
.wc-total .wc-num.warn { color: var(--warning); }
.wc-total .wc-num.bad { color: var(--error); }
.wc-total .wc-label { font-size: 0.8rem; color: var(--secondary); }
.wc-range { font-size: 0.82rem; color: var(--secondary); }
.wc-range strong { color: var(--primary); }
.wc-bar-wrap { display: flex; align-items: center; gap: 8px; }
.wc-bar { width: 160px; height: 10px; background: var(--border); border-radius: 5px; overflow: hidden; position: relative; }
.wc-bar-min { position: absolute; top: -2px; bottom: -2px; width: 2px; background: var(--warning); border-radius: 1px; z-index: 1; }
.wc-bar-fill { height: 100%; border-radius: 5px; transition: width 0.4s ease, background 0.3s; position: relative; z-index: 2; }
.wc-bar-fill.good { background: var(--success); }
.wc-bar-fill.warn { background: var(--warning); }
.wc-bar-fill.bad { background: var(--error); }
.wc-pct { font-size: 0.75rem; color: var(--secondary); min-width: 36px; text-align: right; }
/* ===== Bottom Toolbar ===== */
.bottom-bar {
  position: fixed; bottom: 0; left: var(--sidebar-width, 240px); right: 0;
  background: rgba(255,255,255,0.95); backdrop-filter: blur(12px);
  border-top: 1px solid var(--border);
  box-shadow: 0 -2px 20px rgba(0,0,0,0.06);
  z-index: 100;
  transition: left 0.3s ease;
}
.bottom-inner {
  max-width: 1200px; margin: 0 auto; padding: 12px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
}
.bar-left, .bar-right { display: flex; align-items: center; gap: 8px; }
.btn-bar {
  padding: 8px 18px; border: 1px solid var(--border); border-radius: var(--radius-sm);
  background: #fff; font-family: inherit; font-size: 0.85rem; font-weight: 500;
  color: var(--primary); cursor: pointer; transition: all 0.2s;
  display: inline-flex; align-items: center; gap: 6px;
}
.btn-bar:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-light); }
.btn-bar.primary { background: var(--accent); color: #fff; border-color: var(--accent); }
.btn-bar.primary:hover { background: var(--accent-hover); }
/* ===== Modal (Element UI dialog override) ===== */
.broadcast-dialog .el-dialog {
  border-radius: var(--radius); overflow: hidden;
  box-shadow: var(--shadow-lg); max-width: 900px;
}
.broadcast-dialog .el-dialog__header {
  padding: 20px 24px; border-bottom: 1px solid var(--border);
  margin: 0;
}
.broadcast-dialog .el-dialog__title {
  font-size: 1.1rem; font-weight: 600; color: var(--primary);
}
.broadcast-dialog .el-dialog__body { padding: 24px; max-height: 65vh; overflow-y: auto; }
.broadcast-dialog .el-dialog__footer {
  padding: 16px 24px; border-top: 1px solid var(--border);
  display: flex; justify-content: flex-end; gap: 8px;
}
/* ===== Preview Content ===== */
.preview-section { margin-bottom: 28px; }
.preview-section-title {
  font-size: 1.2rem; font-weight: 700; color: var(--primary);
  padding-bottom: 8px; border-bottom: 2px solid var(--accent);
  margin-bottom: 16px;
}
.preview-meta { font-size: 0.85rem; color: var(--secondary); margin-bottom: 8px; }
.preview-line { font-size: 0.9rem; line-height: 2; margin-bottom: 4px; }
.preview-line .label-a { color: var(--a-color); font-weight: 600; }
.preview-line .label-b { color: var(--b-color); font-weight: 600; }
/* ===== History ===== */
.history-list { display: flex; flex-direction: column; gap: 8px; }
.history-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border: 1px solid var(--border-light); border-radius: var(--radius-sm);
  transition: all 0.2s;
}
.history-item:hover { border-color: var(--accent); background: var(--accent-light); }
.history-date { font-weight: 600; font-size: 0.95rem; }
.history-status { font-size: 0.78rem; color: var(--secondary); }
.history-status .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; }
.history-status .dot.done { background: var(--success); }
.history-status .dot.partial { background: var(--warning); }
.history-actions { display: flex; gap: 4px; }
/* ===== Responsive ===== */
@media (max-width: 768px) {
  .header-inner { padding: 12px 16px; }
  .header h1 { font-size: 1.15rem; }
  .logo-icon { width: 36px; height: 36px; font-size: 16px; }
  .tabs-bar { padding: 12px 12px 0; gap: 4px; }
  .tab-btn { padding: 10px 8px; font-size: 0.82rem; gap: 4px; }
  .tab-btn .tab-icon { font-size: 0.95rem; }
  .tab-btn .tab-time { font-size: 0.68rem; }
  .main-wrap { padding: 0 8px 100px; }
  .section-info { padding: 16px; }
  .section-meta { flex-direction: column; align-items: flex-start; gap: 8px; }
  .guide-box { padding: 10px 12px; }
  .guide-row { flex-direction: column; gap: 4px; }
  .guide-tag { align-self: flex-start; }
  .guide-text { font-size: 0.82rem; }
  .section-fields { grid-template-columns: 1fr; }
  .source-section { padding: 16px; }
  .source-textarea .el-textarea__inner { min-height: 140px !important; font-size: 0.88rem; padding: 12px; }
  .source-actions { flex-wrap: wrap; }
  .source-hint { font-size: 0.74rem; }
  .source-wc { min-width: 100%; margin-left: 0; }
  .generated-section { padding: 16px; }
  .generated-header { margin-bottom: 12px; }
  .para-item { padding: 10px 12px; gap: 8px; }
  .para-label { width: 24px; height: 24px; font-size: 0.75rem; }
  .para-textarea { font-size: 0.88rem; line-height: 1.7; }
  .para-footer { flex-direction: column; align-items: flex-start; gap: 6px; }
  .para-actions { align-self: flex-end; }
  .wordcount-bar { margin: 16px; padding: 12px 16px; flex-direction: column; align-items: stretch; gap: 8px; }
  .wc-left { justify-content: space-between; }
  .wc-total .wc-num { font-size: 1.3rem; }
  .wc-bar-wrap { justify-content: space-between; }
  .wc-bar { flex: 1; height: 8px; }
  .bottom-inner { padding: 10px 12px; gap: 8px; }
  .bar-left, .bar-right { gap: 6px; flex-wrap: wrap; }
  .btn-bar { padding: 7px 12px; font-size: 0.8rem; }
  .broadcast-dialog .el-dialog { max-height: 95vh; margin: 0 8px !important; }
  .broadcast-dialog .el-dialog__header { padding: 16px; }
  .broadcast-dialog .el-dialog__title { font-size: 1rem; }
  .broadcast-dialog .el-dialog__body { padding: 16px; }
  .preview-section { padding: 12px; }
}
@media (max-width: 480px) {
  .header h1 { font-size: 1rem; }
  .header h1 .subtitle { display: none; }
  .tab-btn { padding: 8px 6px; font-size: 0.78rem; }
  .tab-btn .tab-time { display: none; }
  .source-actions { flex-direction: column; }
  .source-actions .el-button { width: 100%; margin-left: 0 !important; }
  .bottom-inner { flex-direction: column; gap: 6px; }
  .bar-left, .bar-right { width: 100%; justify-content: center; }
  .btn-bar { flex: 1; justify-content: center; max-width: 50%; }
}
/* ===== Scrollbar ===== */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: var(--secondary); }
/* ===== Wave animation ===== */
.wave-bars { display: flex; align-items: end; gap: 2px; height: 20px; margin-left: 8px; }
.wave-bars span {
  display: block; width: 3px; background: var(--accent); border-radius: 2px;
  animation: wave 1.2s ease-in-out infinite;
}
.wave-bars span:nth-child(1) { height: 8px; animation-delay: 0s; }
.wave-bars span:nth-child(2) { height: 16px; animation-delay: 0.15s; }
.wave-bars span:nth-child(3) { height: 10px; animation-delay: 0.3s; }
.wave-bars span:nth-child(4) { height: 18px; animation-delay: 0.45s; }
.wave-bars span:nth-child(5) { height: 6px; animation-delay: 0.6s; }
@keyframes wave {
  0%, 100% { transform: scaleY(0.5); opacity: 0.5; }
  50% { transform: scaleY(1); opacity: 1; }
}
</style>
